import React, { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  Users,
  CalendarOff,
  CalendarCheck,
  ClipboardList,
  RefreshCw,
  Building2,
  Award,
  AlertTriangle,
  Clock,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { computeAttendanceDashboardMetrics } from "../../lib/attendanceDashboardMetrics";

function RankList({ darkMode, title, items, emptyLabel, showSub = false }) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        darkMode ? "border-[#3C3C4C] bg-[#242424]" : "border-gray-200 bg-gray-50/90"
      }`}
    >
      <h4
        className={`mb-2 text-xs font-semibold uppercase tracking-wide ${
          darkMode ? "text-gray-300" : "text-gray-700"
        }`}
      >
        {title}
      </h4>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((row, idx) => (
            <li
              key={`${row.name}-${idx}`}
              className="flex items-start justify-between gap-2 text-xs"
            >
              <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">
                {row.name}
                {showSub && row.sub ? (
                  <span className="mt-0.5 block text-[10px] text-gray-400">
                    {row.sub}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-redhalo-navy dark:text-redhalo-mint">
                {row.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MetricCard({ darkMode, label, value, sub, icon: Icon, tone = "default" }) {
  const toneClass =
    tone === "up"
      ? darkMode
        ? "text-emerald-400"
        : "text-emerald-700"
      : tone === "down"
      ? darkMode
        ? "text-rose-400"
        : "text-rose-700"
      : darkMode
      ? "text-gray-100"
      : "text-gray-900";

  return (
    <div
      className={`rounded-lg border p-3 ${
        darkMode
          ? "border-[#3C3C4C] bg-[#242424]"
          : "border-gray-200 bg-gray-50/90 shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
          </div>
          <div className={`mt-1 text-xl font-bold leading-tight ${toneClass}`}>
            {value}
          </div>
          {sub ? (
            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {sub}
            </div>
          ) : null}
        </div>
        {Icon ? (
          <Icon
            className={`h-4 w-4 shrink-0 opacity-70 ${
              darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
            }`}
            aria-hidden
          />
        ) : null}
      </div>
    </div>
  );
}

export default function AttendanceDashboardPanel({
  darkMode = false,
  isFlipped = false,
  onFlip,
  showFlipButton = true,
  employees = [],
  attendanceData = {},
  leaveTypes = [],
  insightItems = [],
  attendanceDetailData = {},
  selectedMonth = 0,
  selectedYear = new Date().getFullYear(),
  reverseMapping = {},
  loading = false,
  leaveTypeLabels = {},
  onPeriodChange,
}) {
  const now = new Date();
  const [dashboardView, setDashboardView] = useState("present");
  const [periodMode, setPeriodMode] = useState("month");
  const [month, setMonth] = useState(selectedMonth + 1);
  const [year, setYear] = useState(selectedYear);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  useEffect(() => {
    if (periodMode !== "range") return;
    if (rangeFrom && rangeTo) return;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    if (!rangeFrom) setRangeFrom(`01-${m}-${y}`);
    if (!rangeTo) setRangeTo(`${lastDay}-${m}-${y}`);
  }, [periodMode, rangeFrom, rangeTo, now]);

  useEffect(() => {
    setMonth(selectedMonth + 1);
    setYear(selectedYear);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (typeof onPeriodChange !== "function") return;
    if (periodMode === "month") {
      onPeriodChange({ periodMode, month, year });
    }
  }, [periodMode, month, year, onPeriodChange]);

  const metrics = useMemo(
    () =>
      computeAttendanceDashboardMetrics({
        employees,
        attendanceData,
        leaveTypes,
        insightItems,
        attendanceDetailData,
        selectedMonth,
        selectedYear,
        reverseMapping,
        periodMode,
        month,
        year,
        rangeFrom,
        rangeTo,
      }),
    [
      employees,
      attendanceData,
      leaveTypes,
      insightItems,
      attendanceDetailData,
      selectedMonth,
      selectedYear,
      reverseMapping,
      periodMode,
      month,
      year,
      rangeFrom,
      rangeTo,
    ]
  );

  const labelForCode = (code) =>
    leaveTypeLabels[code] || leaveTypeLabels[String(code).toUpperCase()] || code;

  const chipClass = (active) =>
    `rounded-full px-3 py-1 text-xs font-semibold transition ${
      active
        ? darkMode
          ? "bg-redhalo-navy text-redhalo-mint"
          : "bg-redhalo-navy text-white"
        : darkMode
        ? "bg-[#2C2C2C] text-gray-300 hover:bg-[#333]"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`;

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 8 }, (_, i) => current - i);
  }, [now]);

  const selectClass = darkMode
    ? "rounded-md border border-[#3C3C4C] bg-[#2C2C2C] px-2 py-1.5 text-xs text-[#E0E0E0] focus:border-redhalo-red focus:outline-none"
    : "rounded-md border border-[#f3d6bf] bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-[#ef6f2e] focus:outline-none";

  const inputClass = darkMode
    ? "rounded-md border border-[#3C3C4C] bg-[#2C2C2C] px-2 py-1.5 text-xs text-[#E0E0E0] focus:border-redhalo-red focus:outline-none"
    : "rounded-md border border-[#f3d6bf] bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-[#ef6f2e] focus:outline-none";

  return (
    <>
      {showFlipButton && (
        <button
          type="button"
          onClick={onFlip}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
            darkMode
              ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
              : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
          }`}
          title={
            isFlipped
              ? "Back to attendance list"
              : "Flip to attendance analytics dashboard"
          }
        >
          <LayoutDashboard className="w-3.5 h-3.5" />
          {isFlipped ? "Back" : "Flip Dashboard"}
        </button>
      )}

      {isFlipped && (
        <div
          className={`min-h-0 flex-1 overflow-y-auto rounded-lg border ${
            darkMode
              ? "border-[#3C3C4C] bg-[#1D1D1D]"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="px-4 py-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3
                  className={`text-lg font-bold tracking-tight ${
                    darkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  Attendance Dashboard
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {metrics.periodLabel}
                  {loading ? " · Updating…" : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "present", label: "Present" },
                  { id: "historical", label: "Historical" },
                  { id: "analytics", label: "Analytics" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setDashboardView(tab.id)}
                    className={chipClass(dashboardView === tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div
              className={`mb-4 rounded-md border p-3 ${
                darkMode ? "border-[#3C3C4C] bg-[#1C1C1C]" : "border-[#165b5d]/30 bg-white"
              }`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Period
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {[
                  { id: "month", label: "Month" },
                  { id: "year", label: "Year" },
                  { id: "range", label: "Range" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setPeriodMode(tab.id)}
                    className={chipClass(periodMode === tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}

                {periodMode === "month" && (
                  <>
                    <select
                      className={selectClass}
                      value={month}
                      onChange={(e) => setMonth(Number(e.target.value))}
                      aria-label="Month"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2000, i, 1).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                    <select
                      className={selectClass}
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      aria-label="Year"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {periodMode === "year" && (
                  <select
                    className={selectClass}
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {periodMode === "range" && (
                  <>
                    <label className="flex flex-col gap-0.5 text-[10px] text-gray-500">
                      From (DD-MM-YYYY)
                      <input
                        type="text"
                        className={inputClass}
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                        placeholder="01-01-2026"
                      />
                    </label>
                    <label className="flex flex-col gap-0.5 text-[10px] text-gray-500">
                      To (DD-MM-YYYY)
                      <input
                        type="text"
                        className={inputClass}
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                        placeholder="31-01-2026"
                      />
                    </label>
                  </>
                )}
              </div>
            </div>

            {metrics.dataScopeNote ? (
              <p className="mb-3 text-[11px] text-amber-600 dark:text-amber-400">
                {metrics.dataScopeNote}
              </p>
            ) : null}

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-8 w-8 animate-spin text-redhalo-red" />
              </div>
            ) : (
              <>
                {dashboardView === "present" && (
                  <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    <MetricCard darkMode={darkMode} label="Employees" value={metrics.headcount} sub={`${metrics.employeesWithData} with data`} icon={Users} />
                    <MetricCard darkMode={darkMode} label="Present rate" value={`${metrics.presentRate}%`} sub={`${metrics.totalPresentDays} present days`} icon={CalendarCheck} tone="up" />
                    <MetricCard darkMode={darkMode} label="Absenteeism" value={`${metrics.absenteeismRate}%`} sub={`${metrics.totalAbsentDays} LOP days`} icon={CalendarOff} tone="down" />
                    <MetricCard darkMode={darkMode} label="Leave days" value={metrics.totalLeaveDays} sub="Policy leave codes" icon={ClipboardList} />
                    <MetricCard darkMode={darkMode} label="Weekly off" value={metrics.totalWoDays} sub={`${metrics.daysInMonth}-day month`} />
                    <MetricCard darkMode={darkMode} label="Public holiday" value={metrics.totalPhDays} sub="PH in grid" />
                    <MetricCard darkMode={darkMode} label="Avg present" value={metrics.avgPresentPerEmployee} sub="Per employee with data" icon={TrendingUp} tone="up" />
                    <MetricCard darkMode={darkMode} label="Pending leave" value={metrics.pendingLeaveRequests} sub="Action insights" icon={Clock} />
                  </div>
                )}

                {dashboardView === "historical" && (
                  <>
                    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                      <MetricCard darkMode={darkMode} label="Approved leave" value={metrics.approvedLeaveRequests} sub="Insight log" icon={CalendarCheck} tone="up" />
                      <MetricCard darkMode={darkMode} label="Rejected leave" value={metrics.rejectedLeaveRequests} sub="Insight log" icon={AlertTriangle} tone="down" />
                      <MetricCard darkMode={darkMode} label="Pending" value={metrics.pendingLeaveRequests} sub="Awaiting approval" icon={Clock} />
                      <MetricCard darkMode={darkMode} label="Regularisation" value={metrics.regularizationChecks} sub="Time edits" icon={Building2} />
                      <MetricCard darkMode={darkMode} label="Period" value={metrics.monthLabel} sub={`${metrics.employeesWithData} employees`} />
                      <MetricCard darkMode={darkMode} label="Present days" value={metrics.totalPresentDays} sub={`Absent: ${metrics.totalAbsentDays}`} tone="up" />
                    </div>
                    <RankList darkMode={darkMode} title="Highest absenteeism — employees" items={metrics.highestAbsentEmployees} emptyLabel="No absent days in period." />
                  </>
                )}

                {dashboardView === "analytics" && (
                  <>
                    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                      <MetricCard darkMode={darkMode} label="Regularisation" value={metrics.regularizationChecks} sub="Punch corrections" icon={BarChart3} />
                      <MetricCard darkMode={darkMode} label="Leave types" value={metrics.leaveRows.length} sub={`${metrics.totalLeaveDays} leave days`} icon={ClipboardList} />
                      <MetricCard darkMode={darkMode} label="Absenteeism" value={`${metrics.absenteeismRate}%`} sub="LOP / unpaid" tone="down" />
                      <MetricCard darkMode={darkMode} label="Present rate" value={`${metrics.presentRate}%`} sub="Tracked days" tone="up" />
                    </div>
                    <div className="grid gap-3 lg:grid-cols-3">
                      <RankList darkMode={darkMode} title="Departments — absenteeism" items={metrics.topAbsentDepts} emptyLabel="No absent days." />
                      <RankList darkMode={darkMode} title="Departments — present days" items={metrics.topPresentDepts} emptyLabel="No present days." />
                      <RankList darkMode={darkMode} title="Departments — regularisation" items={metrics.topRegDepts} emptyLabel="No regularisation." />
                    </div>
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      <RankList darkMode={darkMode} title="Top 5 — full attendance" items={metrics.topFullAttendance} emptyLabel="No present days." showSub />
                      <div className={`rounded-lg border p-3 ${darkMode ? "border-[#3C3C4C] bg-[#242424]" : "border-gray-200 bg-gray-50/90"}`}>
                        <h4 className={`mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                          <Award className="h-3.5 w-3.5" aria-hidden />
                          Leave breakdown
                        </h4>
                        {metrics.leaveRows.length === 0 ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No leave codes for this period.</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {metrics.leaveRows.map((row) => (
                              <li key={row.code} className="flex items-center justify-between gap-2 text-xs">
                                <span className="truncate text-gray-700 dark:text-gray-200">
                                  {labelForCode(row.code)}
                                  <span className="ml-1 text-gray-400">({row.code})</span>
                                </span>
                                <span className="shrink-0 font-semibold tabular-nums text-redhalo-navy dark:text-redhalo-mint">{row.count}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
